// Fetch impacted applications list from Azure Function endpoint /fetch_bat_bp
const mode = import.meta.env.MODE;
let baseurl: string;

if (mode === 'production') {
  baseurl = 'https://askawatch.cbre.com';
  console.log('FetchIncidents: Running in PRODUCTION mode');
} else if (mode === 'staging') {
  baseurl = 'https://askawatch-test.cbre.com';
  console.log('FetchIncidents: Running in non prod mode');
} else {
  baseurl = 'http://localhost:8000';
  console.log('FetchIncidents: Running in DEVELOPMENT mode');
}

const APPROVALS_PENDING_URL =
  (import.meta as any).env.VITE_APPROVALS_PENDING_URL ||
  `${baseurl}/api/approvals/pending`;

const APPROVALS_DECIDE_URL =
  (import.meta as any).env.VITE_APPROVALS_DECIDE_URL ||
  `${baseurl}/api/approvals/decide`;

const APPROVALS_FETCH_URL =
  (import.meta as any).env.VITE_APPROVALS_FETCH_URL ||
  `${baseurl}/api/approvals/fetch_approvals`;

const APPROVALS_SET_STATUS_URL =
  (import.meta as any).env.VITE_APPROVALS_SET_STATUS_URL ||
  `${baseurl}/api/approvals/set_approval_status`;

// Backend expanded item (updated for new API shape)
export interface ApprovalFetchItem {
  approval_id: number | null;           // now nullable
  update_id: number;
  incident_id?: string;                 // new field name from backend
  incident_number?: string;             // keep for backward compatibility
  approval_email: string | null;
  status: 'pending' | 'approved' | 'rejected';
  submitted_by: string | null;
  submitted_at: string | null;
  update_text: string;                  // now holds configuration_class context
  update_updated_by?: string;           // optional (may be absent)
}

// Simplified UI item (previous PendingApproval)
export interface PendingApproval {
  id: number;
  incident_number: string;
  update_id: number;
  update_text: string;
  submitted_by: string;
  submitted_at: string;
  status: string;
}

// Fetch pending approvals
export async function fetchPendingApprovals(): Promise<{
  success: boolean;
  pending: PendingApproval[];
}> {
  const r = await fetch(APPROVALS_PENDING_URL, { method: 'GET' });
  if (!r.ok) throw new Error(`Fetch pending approvals failed: ${r.status}`);
  return r.json();
}

// Fetch approvals (statuses may be ignored by backend now; keep signature for compat)
export async function fetchApprovals(params?: {
  statuses?: ('pending' | 'approved' | 'rejected')[];
  limit?: number;
}): Promise<{
  success: boolean;
  count: number;
  approvals: ApprovalFetchItem[];
}> {
  const body = JSON.stringify({
    // backend currently only uses limit; send statuses for forward compatibility
    statuses: params?.statuses || [],
    limit: params?.limit ?? 200
  });
  const r = await fetch(APPROVALS_FETCH_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body
  });
  if (!r.ok) throw new Error(`Fetch approvals failed: ${r.status}`);
  return r.json();
}

// Decide (approve/reject)
export async function decideApproval(
  approvalId: number,
  approve: boolean,
  comment?: string
): Promise<{
  success: boolean;
  id: number;
  status: 'approved' | 'rejected';
}> {
  const r = await fetch(`${APPROVALS_DECIDE_URL}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: approvalId,
      approve,
      comment: comment || ''
    })
  });
  if (!r.ok) throw new Error(`Approval decision failed: ${r.status}`);
  return r.json();
}

export async function setApprovalStatus(
  approvalId: number,
  updateId: number,
  newStatus: 'approved' | 'rejected'
): Promise<{
  success: boolean;
  approval_id: number;
  new_status: 'approved' | 'rejected';
}> {
  if (!['approved', 'rejected'].includes(newStatus)) {
    throw new Error(`Invalid status: ${newStatus}`);
  }
  const url = APPROVALS_SET_STATUS_URL;
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ 
      approval_id: approvalId,
      update_id: updateId,
      new_status: newStatus })
  });
  if (!r.ok) throw new Error(`Set approval status failed: ${r.status}`);
  return r.json();
}